import React, { useState, useEffect } from "react";
import {
  Row,
  Col,
  Spin,
  Form,
  Card,
  DatePicker,
  InputNumber,
  Input,
  Dropdown,
} from "antd";
import { InfoCircleOutlined } from "@ant-design/icons";
import { Tooltip } from "antd";
import { connect } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import dayjs from "dayjs";

import MainLayout from "../../../components/layouts/enhancement/MainLayout";
import TopContent from "../../../components/layouts/enhancement/TopContent";
import ChevronDown from "@untitled-ui/icons-react/build/esm/ChevronDown";
import Settings01 from "@untitled-ui/icons-react/build/esm/Settings01";
import cnt from "../../../services/definedConstant";
import { rightAction } from "../../../redux/actions";
import {
  labelConfig,
  staticConfig,
  apiConfig,
} from "../treasuryContractBooking.config";
import { BOM } from "../../../services/definedMethod";

// ── Status badge ───────────────────────────────────────────────────────────
const STATUS_COLORS = {
  CONFIRMED: { bg: "#ECFDF3", color: "#027A48" },
  INSTRUCTED: { bg: "#EFF8FF", color: "#175CD3" },
  PENDING: { bg: "#FFFAEB", color: "#B54708" },
  FAILED: { bg: "#FEF3F2", color: "#B42318" },
};

const StatusBadge = ({ status }) => {
  const s = STATUS_COLORS[status] || { bg: "#F2F4F7", color: "#344054" };
  return (
    <span
      style={{
        display: "inline-block",
        padding: "4px 12px",
        borderRadius: 99,
        fontSize: 13,
        fontWeight: 600,
        background: s.bg,
        color: s.color,
      }}
    >
      {status}
    </span>
  );
};

// ── Component ──────────────────────────────────────────────────────────────
const ViewSettlement = (props) => {
  const [form] = Form.useForm();
  const [isLoading, setIsLoading] = useState(false);
  const [status, setStatus] = useState("PENDING");
  const navigate = useNavigate();
  const { id } = useParams();

  useEffect(() => {
    if (id) fetchSettlementDetail(id);
  }, [id]);

  const fetchSettlementDetail = async (settlementId) => {
    try {
      setIsLoading(true);
      // const res = await BOM.SendReq(apiConfig.getSettlementById, { id: settlementId });
      const data = {
        id: 1,
        tradeId: 1,
        settlementDate: "2026-02-24",
        cashAmount: 49_250_000.0,
        securityFaceValue: 50_000_000.0,
        status: "CONFIRMED",
        confirmedAt: "2026-02-24T10:30:00",
        externalReference: "SETTLE-2026-001",
      };

      setStatus(data.status);
      form.setFieldsValue({
        tradeId: data.tradeId,
        settlementDate: dayjs(data.settlementDate),
        cashAmount: data.cashAmount,
        securityFaceValue: data.securityFaceValue,
        status: data.status,
        confirmedAt: data.confirmedAt
          ? dayjs(data.confirmedAt).format("YYYY-MM-DD HH:mm:ss")
          : "—",
        externalReference: data.externalReference,
      });
    } catch (error) {
      BOM.AlertMsg(
        cnt.DANGER,
        error?.message || "Failed to load settlement details.",
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleAction = ({ key }) => {
    switch (key) {
      case "instruct":
        /* BOM.SendReq(apiConfig.settleTrade) */ break;
        break;
      case "confirm":
        // navigate(`/settlement/${id}/confirm`);
        navigate(`/settlement/confirm`);
        break;
      case "fail":
        
        // navigate(`/settlement/${id}/fail`);
        navigate(`/settlement/fail`);
        break;
      default:
        break;
    }
  };

  const actionMenuItems = [
    { key: "instruct", label: "Instruct Settlement" },
    { key: "confirm", label: "Confirm Settlement" },
    { type: "divider" },
    {
      key: "fail",
      label: <span style={{ color: "#B42318" }}>Fail Settlement</span>,
    },
  ];

  const ds = { height: 44, background: "#F9FAFB", cursor: "not-allowed" };

  return (
    <MainLayout pageItems={staticConfig.manageSettlement}>
      <TopContent pageTitle={labelConfig.settlement} />

      <Spin spinning={isLoading} size="large">
        <div style={{ padding: "24px" }}>
          <Card
            style={{
              boxShadow: "0px 0px 30px 3px rgba(0,0,0,0.1)",
              borderRadius: 20,
            }}
          >
            {/* Header */}
            <Row
              justify="space-between"
              align="middle"
              style={{ marginBottom: 24 }}
            >
              <Col style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <h3 style={{ margin: 0, fontWeight: 600 }}>
                  Settlement Details
                </h3>
                {/* <StatusBadge status={status} /> */}
              </Col>
              <Col>
                <Dropdown
                  menu={{ items: actionMenuItems, onClick: handleAction }}
                  trigger={["click"]}
                  placement="bottomRight"
                >
                  <button
                    type="button"
                    style={{
                      height: 44,
                      padding: "0 20px",
                      borderRadius: 8,
                      border: "1px solid #D0D5DD",
                      background: "#fff",
                      color: "#344054",
                      fontWeight: 600,
                      fontSize: 14,
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                    }}
                  >
                    <Settings01 width={16} height={16} color="#344054" />
                    Actions
                    <ChevronDown width={16} height={16} color="#667085" />
                  </button>
                </Dropdown>
              </Col>
            </Row>

            <Form layout="vertical" form={form}>
              {/* Trade ID & Settlement Date */}
              <Row>
                <Col span={12} style={{ padding: "0 8px" }}>
                  <Form.Item
                    label={
                      <span>
                        Trade ID{" "}
                        <Tooltip title="The trade this settlement is linked to.">
                          <InfoCircleOutlined
                            style={{ marginLeft: 4, color: "#667085" }}
                          />
                        </Tooltip>
                      </span>
                    }
                    name="tradeId"
                  >
                    <InputNumber style={{ width: "100%", ...ds }} disabled />
                  </Form.Item>
                </Col>
                <Col span={12} style={{ padding: "0 8px" }}>
                  <Form.Item
                    label={
                      <span>
                        Settlement Date{" "}
                        <Tooltip title="The date on which settlement is due.">
                          <InfoCircleOutlined
                            style={{ marginLeft: 4, color: "#667085" }}
                          />
                        </Tooltip>
                      </span>
                    }
                    name="settlementDate"
                  >
                    <DatePicker
                      style={{
                        width: "100%",
                        height: 44,
                        background: "#F9FAFB",
                        cursor: "not-allowed",
                      }}
                      format="YYYY-MM-DD"
                      disabled
                    />
                  </Form.Item>
                </Col>
              </Row>

              {/* Cash Amount & Security Face Value */}
              <Row>
                <Col span={12} style={{ padding: "0 8px" }}>
                  <Form.Item
                    label={
                      <span>
                        Cash Amount{" "}
                        <Tooltip title="The cash leg of the settlement.">
                          <InfoCircleOutlined
                            style={{ marginLeft: 4, color: "#667085" }}
                          />
                        </Tooltip>
                      </span>
                    }
                    name="cashAmount"
                  >
                    <InputNumber
                      style={{ width: "100%", ...ds }}
                      formatter={(v) =>
                        v ? `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",") : ""
                      }
                      parser={(v) => v?.replace(/,/g, "")}
                      precision={2}
                      disabled
                    />
                  </Form.Item>
                </Col>
                <Col span={12} style={{ padding: "0 8px" }}>
                  <Form.Item
                    label={
                      <span>
                        Security Face Value{" "}
                        <Tooltip title="The nominal face value of securities being delivered or received.">
                          <InfoCircleOutlined
                            style={{ marginLeft: 4, color: "#667085" }}
                          />
                        </Tooltip>
                      </span>
                    }
                    name="securityFaceValue"
                  >
                    <InputNumber
                      style={{ width: "100%", ...ds }}
                      formatter={(v) =>
                        v ? `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",") : ""
                      }
                      parser={(v) => v?.replace(/,/g, "")}
                      precision={2}
                      disabled
                    />
                  </Form.Item>
                </Col>
              </Row>

              {/* External Reference & Confirmed At */}
              <Row>
                <Col span={12} style={{ padding: "0 8px" }}>
                  <Form.Item
                    label={
                      <span>
                        External Reference{" "}
                        <Tooltip title="The external reference number for this settlement.">
                          <InfoCircleOutlined
                            style={{ marginLeft: 4, color: "#667085" }}
                          />
                        </Tooltip>
                      </span>
                    }
                    name="externalReference"
                  >
                    <Input style={{ ...ds }} disabled />
                  </Form.Item>
                </Col>
                <Col span={12} style={{ padding: "0 8px" }}>
                  <Form.Item
                    label={
                      <span>
                        Confirmed At{" "}
                        <Tooltip title="The timestamp when this settlement was confirmed.">
                          <InfoCircleOutlined
                            style={{ marginLeft: 4, color: "#667085" }}
                          />
                        </Tooltip>
                      </span>
                    }
                    name="confirmedAt"
                  >
                    <Input style={{ ...ds }} disabled />
                  </Form.Item>
                </Col>
              </Row>

              <div
                style={{ borderTop: "1px solid #EAECF0", margin: "8px 0 20px" }}
              />

              <Row justify="end">
                <Col>
                  <button
                    type="button"
                    onClick={() => navigate(-1)}
                    style={{
                      height: 44,
                      padding: "0 24px",
                      borderRadius: 8,
                      border: "1px solid #D0D5DD",
                      background: "#fff",
                      color: "#344054",
                      fontWeight: 600,
                      fontSize: 14,
                      cursor: "pointer",
                    }}
                  >
                    Back
                  </button>
                </Col>
              </Row>
            </Form>
          </Card>
        </div>
      </Spin>
    </MainLayout>
  );
};

const mapStateToProps = (state) => ({ rightBtn: state.RightSideBtn });
export default connect(mapStateToProps, { rightAction })(ViewSettlement);
